import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Checkbox,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceCsrf,
  useServiceCsrfSites,
  useUpdateServiceCsrf,
  defaultCsrf,
  CSRF_METHODS,
  type CsrfConfig,
  type CsrfRule,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { CheckboxField } from '@/pages/services/waap/components/CheckboxField';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './CsrfForm.module.css';

const subHeading = (title: string, desc: string) => (
  <>
    <div className={styles.subTitle}>{title}</div>
    <div className={styles.subDesc}>
      {desc}
    </div>
  </>
);

const TRUSTED_SITES_NOTE =
  'The referers on this list are added to the CSRF exception list.\n' +
  '1. Sites can be domain names or IPv4 addresses, without port numbers.\n' +
  '2. * (wildcard) is allowed (e.g. www.abc.* and *.abc.com).\n' +
  '3. Case-insensitive.';

/**
 * Edit one website's CSRF Protection rule (the site's `config.waf_csrf_strategy`).
 * Mirrors the SwiftFederation portal layout: Enable, then the URL Paths list
 * (paths whose referers are checked), then the Trusted Sites list (the referer
 * exception list, persisted as the comma-separated `csrf_whitelist`).
 * Route: aiwaf/advanced/csrf/:siteId.
 */
export function CsrfForm() {
  const navigate = useNavigate();
  const { serviceId, siteId } = useParams<{ serviceId: string; siteId: string }>();
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;

  const { data: existing, isLoading: loadingExisting } = useServiceCsrf(serviceId, siteId);
  const { data: sites = [] } = useServiceCsrfSites(serviceId);
  const domain = useMemo(
    () => sites.find((s) => String(s.site_id) === siteId)?.domain ?? '',
    [sites, siteId],
  );

  const [form, setForm] = useState<CsrfConfig>(() => defaultCsrf());
  const set = <K extends keyof CsrfConfig>(key: K, value: CsrfConfig[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (existing) setForm(existing);
  }, [existing]);

  // ---- URL Paths list -----------------------------------------------------
  const urlEmpty = { path: '', method: CSRF_METHODS[0] as string, caseSensitive: false, comment: '' };
  const [urlDraft, setUrlDraft] = useState(urlEmpty);
  const addRule = () => {
    if (!urlDraft.path.trim()) return;
    const entry: CsrfRule = {
      path: urlDraft.path.trim(),
      method: urlDraft.method,
      case_sensitive: urlDraft.caseSensitive,
      comment: urlDraft.comment.trim(),
    };
    set('rules', [...form.rules, entry]);
    setUrlDraft(urlEmpty);
  };
  const removeRule = (i: number) => set('rules', form.rules.filter((_, idx) => idx !== i));
  const editRule = (i: number) => {
    const r = form.rules[i];
    setUrlDraft({
      path: r.path,
      method: r.method || CSRF_METHODS[0],
      caseSensitive: r.case_sensitive,
      comment: r.comment,
    });
    removeRule(i);
  };

  // ---- Trusted Sites list (csrf_whitelist, comma-separated) ----------------
  const trusted = useMemo(
    () => form.whitelist.split(',').map((s) => s.trim()).filter(Boolean),
    [form.whitelist],
  );
  const setTrusted = (arr: string[]) => set('whitelist', arr.join(','));
  const [siteDraft, setSiteDraft] = useState('');
  const addTrusted = () => {
    const v = siteDraft.trim();
    if (!v || trusted.includes(v)) {
      setSiteDraft('');
      return;
    }
    setTrusted([...trusted, v]);
    setSiteDraft('');
  };
  const removeTrusted = (i: number) => setTrusted(trusted.filter((_, idx) => idx !== i));
  const editTrusted = (i: number) => {
    setSiteDraft(trusted[i]);
    removeTrusted(i);
  };

  const update = useUpdateServiceCsrf(serviceId ?? '', siteId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'csrf_protection', 'edit');
      show({ tone: 'success', title: 'CSRF protection saved' });
      navigate(`${base}/aiwaf/advanced?tab=csrf`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const submit = () => update.mutate(form);

  if (loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skelForm} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
              { label: 'Cross-Site Request Forgery (CSRF) Protection' },
            ]}
          />
        }
        title={domain || 'CSRF Protection'}
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>CSRF Protection</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGridLg}>
              <CheckboxField
                checked={form.enabled}
                onChange={(v) => set('enabled', v)}
                label="Enable"
                description="Check request referers against the URL list below to verify whether they have cross-site access."
              />

              {form.enabled && (
                <>
                  {/* URL Paths (top) */}
                  <div>
                    {subHeading('URL Paths', 'Add paths that need to be checked.')}
                    <div className={styles.condRow}>
                      <Field label="URL Path" required>
                        {(props) => (
                          <Input
                            {...props}
                            value={urlDraft.path}
                            maxLength={512}
                            placeholder="https://example.com/page"
                            className={styles.urlInput}
                            onChange={(e) => setUrlDraft((d) => ({ ...d, path: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addRule();
                              }
                            }}
                          />
                        )}
                      </Field>
                      <Field label="Case Sensitive">
                        {() => (
                          <label className={styles.checkLabelTall}>
                            <Checkbox
                              checked={urlDraft.caseSensitive}
                              onCheckedChange={(v) => setUrlDraft((d) => ({ ...d, caseSensitive: !!v }))}
                            />
                            <span className={styles.muted}>Enable</span>
                          </label>
                        )}
                      </Field>
                      <Field label="HTTP Method" required>
                        {() => (
                          <Select
                            value={urlDraft.method}
                            onValueChange={(v) => setUrlDraft((d) => ({ ...d, method: v }))}
                          >
                            <SelectTrigger className={styles.methodSelect}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CSRF_METHODS.map((m) => (
                                <SelectItem key={m} value={m}>
                                  {m}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </Field>
                      <Field label="Description">
                        {(props) => (
                          <Input
                            {...props}
                            value={urlDraft.comment}
                            placeholder="Description"
                            className={styles.descInput}
                            onChange={(e) => setUrlDraft((d) => ({ ...d, comment: e.target.value }))}
                          />
                        )}
                      </Field>
                      <Button variant="secondary" startIcon={<Plus size={14} />} onClick={addRule}>
                        Add
                      </Button>
                    </div>

                    <CsrfRuleTable rows={form.rules} onEdit={editRule} onRemove={removeRule} />
                  </div>

                  {/* Trusted Sites (bottom) */}
                  <div>
                    {subHeading('Trusted Sites', TRUSTED_SITES_NOTE)}
                    <div className={styles.trustedRow}>
                      <Input
                        value={siteDraft}
                        placeholder="Trusted site (e.g. www.abc, *.abc.com)"
                        className={styles.trustedInput}
                        onChange={(e) => setSiteDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addTrusted();
                          }
                        }}
                      />
                      <Button variant="secondary" startIcon={<Plus size={14} />} onClick={addTrusted}>
                        Add
                      </Button>
                    </div>

                    <TrustedSiteTable rows={trusted} onEdit={editTrusted} onRemove={removeTrusted} />
                  </div>
                </>
              )}
            </div>
            </CardBody>
          </Card>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/advanced?tab=csrf`)}>
              Cancel
            </Button>
            <Button disabled={update.isPending} onClick={submit}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
    </Container>
  );
}

/** URL Paths table — URL Path / Case Sensitive / HTTP Method / Description + Edit/Delete. */
function CsrfRuleTable({
  rows,
  onEdit,
  onRemove,
}: {
  rows: CsrfRule[];
  onEdit: (i: number) => void;
  onRemove: (i: number) => void;
}) {
  const cols = '2fr 1fr 1fr 1.5fr auto';
  return (
    <div className={styles.table}>
      <div className={styles.tableHead} style={{ gridTemplateColumns: cols }}>
        <div className={styles.cell}>URL Path</div>
        <div className={styles.cell}>Case Sensitive</div>
        <div className={styles.cell}>HTTP Method</div>
        <div className={styles.cell}>Description</div>
        <div className={styles.cell}>Operation</div>
      </div>
      {rows.length === 0 ? (
        <div className={`${styles.cell} ${styles.cellEmpty}`}>
          No data
        </div>
      ) : (
        rows.map((r, i) => (
          <div key={i} className={styles.tableRow} style={{ gridTemplateColumns: cols }}>
            <div className={styles.cell}>{r.path}</div>
            <div className={styles.cell}>{r.case_sensitive ? 'true' : 'false'}</div>
            <div className={styles.cell}>{r.method || 'ALL'}</div>
            <div className={styles.cell}>{r.comment || '—'}</div>
            <div className={`${styles.cell} ${styles.cellActions}`}>
              <IconButton aria-label="Edit" variant="ghost" onClick={() => onEdit(i)}>
                <Pencil size={16} />
              </IconButton>
              <IconButton aria-label="Remove" variant="ghost" onClick={() => onRemove(i)}>
                <Trash2 size={16} />
              </IconButton>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/** Trusted Sites table — Trusted Site + Edit/Delete. */
function TrustedSiteTable({
  rows,
  onEdit,
  onRemove,
}: {
  rows: string[];
  onEdit: (i: number) => void;
  onRemove: (i: number) => void;
}) {
  if (rows.length === 0) return null;
  const cols = '1fr auto';
  return (
    <div className={styles.table}>
      <div className={styles.tableHead} style={{ gridTemplateColumns: cols }}>
        <div className={styles.cell}>Trusted Site</div>
        <div className={styles.cell}>Operation</div>
      </div>
      {rows.map((r, i) => (
        <div key={i} className={styles.tableRow} style={{ gridTemplateColumns: cols }}>
          <div className={styles.cell}>{r}</div>
          <div className={`${styles.cell} ${styles.cellActions}`}>
            <IconButton aria-label="Edit" variant="ghost" onClick={() => onEdit(i)}>
              <Pencil size={16} />
            </IconButton>
            <IconButton aria-label="Remove" variant="ghost" onClick={() => onRemove(i)}>
              <Trash2 size={16} />
            </IconButton>
          </div>
        </div>
      ))}
    </div>
  );
}
